import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart, lineDetails } from "../lib/cart";
import LocationPicker, { feeZoneFor } from "../components/LocationPicker";
import SavedAddressCard from "../components/SavedAddressCard";
import { Checkbox } from "../components/ui/checkbox";
import { loadSavedAddress, saveAddress, forgetAddress } from "../lib/savedAddress";
import RewardBox from "../components/RewardBox";
import { clearPendingReward } from "../lib/spin";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { ArrowLeft, Smartphone, CreditCard, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear, refreshPrices } = useCart();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const saved = useMemo(() => loadSavedAddress(), []);
  const [editing, setEditing] = useState(!saved);
  const [remember, setRemember] = useState(true);
  const [customerName, setCustomerName] = useState(saved?.customer_name || "");
  const [phone, setPhone] = useState(saved?.phone || "");
  const [building, setBuilding] = useState(saved?.building || "");
  const [doorNumber, setDoorNumber] = useState(saved?.door_number || "");
  const [floor, setFloor] = useState(saved?.floor || "");
  const [pin, setPin] = useState(saved ? { lat: saved.lat, lng: saved.lng } : null);
  const [instructions, setInstructions] = useState(saved?.instructions || "");
  const [email, setEmail] = useState(saved?.email || "");
  const [payment, setPayment] = useState("mpesa");
  const [submitting, setSubmitting] = useState(false);
  const placedRef = useRef(false);

  useEffect(() => { api.get("/zones").then((r) => setZones(r.data)); refreshPrices().catch(() => {}); }, [refreshPrices]);
  useEffect(() => { if (items.length === 0 && !placedRef.current) navigate("/"); }, [items.length, navigate]);

  const zoneCfg = feeZoneFor(pin, zones);
  const zone = zoneCfg?.name || null;
  const deliveryFee = zoneCfg?.fee || 0;
  const [reward, setReward] = useState(null);
  const discount = reward?.discount || 0;
  const total = subtotal - discount + deliveryFee;

  const onPin = (p) => setPin(p);

  const validate = () => {
    if (customerName.trim().length < 2) return "Please enter your name";
    if (!/^0[17]\d{8}$/.test(phone.replace(/\s+/g, ""))) return "Enter a valid Kenyan phone (e.g. 0712 345 678)";
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(email.trim())) return "Please enter a valid email address";
    if (building.trim().length < 2) return "Please enter your apartment or building name";
    if (!doorNumber.trim()) return "Please enter your house or door number";
    if (!pin) return "Please drop a pin on the map";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); if (!editing) setEditing(true); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/orders", {
        customer_name: customerName.trim(),
        phone: phone.replace(/\s+/g, ""),
        building: building.trim(),
        door_number: doorNumber.trim(),
        floor: floor.trim(),
        lat: pin.lat,
        lng: pin.lng,
        zone,
        instructions,
        email: email.trim() || null,
        payment_method: payment,
        reward_code: reward?.code || null,
        reward_item_id: reward?.item_id || null,
        items: items.map((i) => ({ item_id: i.item_id, quantity: i.quantity, side: i.side || null, extra_ids: (i.extras || []).map((e) => e.id) })),
      });
      placedRef.current = true;
      if (remember) {
        saveAddress({ customer_name: customerName.trim(), phone: phone.replace(/\s+/g, ""), building: building.trim(), door_number: doorNumber.trim(),
          floor: floor.trim(), instructions, email: email.trim(), zone, lat: pin.lat, lng: pin.lng });
      } else {
        forgetAddress();
      }
      clear();
      if (reward) clearPendingReward();
      navigate(`/order/${res.data.id}`, { state: { freshOrder: true } });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="mlobox-gradient text-white sticky top-0 z-20 shadow">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button data-testid="back-to-menu" onClick={() => navigate("/")} className="rounded-full bg-white/15 hover:bg-white/25 p-2">
            <ArrowLeft size={18} />
          </button>
          <div className="font-display font-extrabold text-lg">Checkout</div>
        </div>
      </header>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-4 py-6 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {!editing && saved && (
            <SavedAddressCard
              address={{ customer_name: customerName, phone, email, building, door_number: doorNumber, floor, zone }}
              onChange={() => setEditing(true)}
              onForget={() => { forgetAddress(); setRemember(false); setEditing(true); toast.success("Saved address removed from this phone"); }}
            />
          )}
          {editing && (<>
          <section className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
            <h2 className="font-display font-bold text-gray-900">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="checkout-name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Jane Wanjiku" />
              </div>
              <div>
                <Label htmlFor="phone">Phone (Kenyan)</Label>
                <Input id="phone" data-testid="checkout-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0712 345 678" inputMode="tel" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="email">Email (optional, for your confirmation and receipt)</Label>
                <Input id="email" type="email" data-testid="checkout-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" inputMode="email" />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
            <h2 className="font-display font-bold text-gray-900">Delivery address</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2">
                <Label htmlFor="bld">Apartment / building name</Label>
                <Input id="bld" data-testid="checkout-building" value={building} onChange={(e) => setBuilding(e.target.value)} placeholder="e.g. Rose Court Apartments" />
              </div>
              <div>
                <Label htmlFor="door">Door no.</Label>
                <Input id="door" data-testid="checkout-door" value={doorNumber} onChange={(e) => setDoorNumber(e.target.value)} placeholder="e.g. B12" />
              </div>
              <div>
                <Label htmlFor="floor">Floor</Label>
                <Input id="floor" data-testid="checkout-floor" value={floor} onChange={(e) => setFloor(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div>
              <Label>Pin your location</Label>
              <LocationPicker pin={pin} onPin={onPin} />
            </div>
            <div>
              <Label htmlFor="ins">Delivery instructions (optional)</Label>
              <Textarea id="ins" data-testid="checkout-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Ring bell at Gate B" />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-address" />
              Remember my details and address on this phone
            </label>
          </section>
          </>)}

          <RewardBox items={items} email={email.trim()} reward={reward} onChange={setReward} />

          <section className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
            <h2 className="font-display font-bold text-gray-900">Payment method</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="grid sm:grid-cols-2 gap-3">
              <label htmlFor="pm-mpesa" data-testid="pay-mpesa" className={`cursor-pointer rounded-xl border p-4 flex gap-3 items-start transition-all ${payment === "mpesa" ? "border-[#CC1A00] bg-[#FFF1EE]" : "border-gray-200"}`}>
                <RadioGroupItem id="pm-mpesa" value="mpesa" className="mt-1" />
                <div>
                  <div className="flex items-center gap-2 font-semibold text-gray-900"><Smartphone size={16} className="text-[#00A859]" /> M-Pesa STK Push</div>
                  <p className="text-xs text-gray-500 mt-1">A prompt is sent to your phone to confirm payment.</p>
                </div>
              </label>
              <label htmlFor="pm-card" data-testid="pay-card" className={`cursor-pointer rounded-xl border p-4 flex gap-3 items-start transition-all ${payment === "card" ? "border-[#CC1A00] bg-[#FFF1EE]" : "border-gray-200"}`}>
                <RadioGroupItem id="pm-card" value="card" className="mt-1" />
                <div>
                  <div className="flex items-center gap-2 font-semibold text-gray-900"><CreditCard size={16} className="text-gray-700" /> Credit / Debit Card</div>
                  <p className="text-xs text-gray-500 mt-1">Visa, Mastercard — secured checkout.</p>
                </div>
              </label>
            </RadioGroup>
            <p className="text-[11px] text-gray-400 flex items-center gap-1"><Lock size={10} /> Demo mode — no real money moves.</p>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24 space-y-3">
            <h3 className="font-display font-bold text-gray-900">Order summary</h3>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {items.map((i) => (
                <div key={i.key} className="flex justify-between gap-3 text-sm">
                  <span className="text-gray-700">{i.quantity}× {i.name}{lineDetails(i) && <span className="block text-[11px] text-gray-500">{lineDetails(i)}</span>}</span>
                  <span className="font-semibold whitespace-nowrap">KSh {i.price * i.quantity}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-100 pt-3 space-y-1 text-sm">
              <div className="flex justify-between gap-2"><span className="text-gray-500">Subtotal</span><span className="whitespace-nowrap">KSh {subtotal}</span></div>
              {reward && (
                <div className="flex justify-between gap-2 text-emerald-700" data-testid="checkout-reward-line">
                  <span>{reward.type === "free_item" ? `Free: ${reward.free_options?.find((o) => o.id === reward.item_id)?.name || "item"}` : reward.label}</span>
                  <span className="whitespace-nowrap">{discount ? `- KSh ${discount}` : "KSh 0"}</span>
                </div>
              )}
              <div className="flex justify-between gap-2"><span className="text-gray-500">Delivery fee</span><span className="whitespace-nowrap" data-testid="checkout-delivery-fee">{pin ? `KSh ${deliveryFee}` : "Add your pin"}</span></div>
              <div className="flex justify-between pt-2 border-t border-gray-100"><span className="font-semibold">Total</span><span data-testid="checkout-total" className="font-extrabold text-[#CC1A00]">KSh {total}</span></div>
            </div>
            <Button type="submit" disabled={submitting} data-testid="place-order-btn" className="w-full mlobox-gradient text-white font-semibold rounded-full py-6 hover:brightness-110 shadow">
              {submitting ? "Placing order..." : `Pay KSh ${total}`}
            </Button>
          </div>
        </aside>
      </form>
    </div>
  );
}
